import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { fill, isNil } from 'lodash';

import { navigate } from 'libs/router';

import Page from 'components/page/Page';
import Tooltip from 'components/Tooltip';
import Spinner from 'components/spinner/Spinner';
import Pagination from 'components/pagination/Pagination';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import { ModalFrameEntity, ModalFrameMessage } from 'components/modal';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import AutofillButton from '../../components/proposal-tile-v2/components/autofill-button/AutofillButton';
import ProposalTile from '../../components/proposal-tile-v2/ProposalTile';
import ProposalPageHeader from '../../components/page-header/ProposalPageHeader';
import ProposalTileFilter from '../../components/proposal-tiles-filter/ProposalTileFilter';
import ProposalNoResults from '../../components/no-results/ProposalNoResults';

import { success } from 'redux-store/reducers/common-reducer';
import { fetchList, removeSingleInfluencerFromList, resetLists } from 'modules/lists/reducers/lists';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { loadAllCreatorsContent, loadCreatorList, resetList, setMedia, updateMemberEntry } from '../../reducers/proposal-page';
import { loadCreatorListInfo, resetInfo, updateListInfo } from '../../reducers/proposal-page-info';
import { getRecentMediaExceptGivenUrls } from '../../reducers/creator-recent-media';
import { downloadCreatorProposal } from 'modules/proposal/reducers/proposal-report';

import { __t, applyTranslations } from 'utils/language';
import { getManageListStatus } from 'utils/list';
import { hasProposalPage } from 'utils/realm-outfits';
import { collectActiveChannels } from 'modules/proposal/components/proposal-tile-v2/utils/tile-utils';
import {
  DEFAULT_LIST_CHANNELS,
  DEFAULT_MEMBER_CUSTOM_CHANNELS,
  POSTS_AMOUNT_INSTAGRAM,
  POSTS_AMOUNT_TIKTOK,
  getGroupedMembers,
  getNewSort,
  isTileIncomplete
} from 'modules/proposal/utils/creator-proposal-utils';

import type {
  Breadcrumb,
  List,
  ListConfirmModalProps,
  LocationWindow,
  PaginationLinks,
  PaginationMeta,
  ProposalCreatorProfile,
  ProposalImage,
  ProposalListInfo,
  ProposalListMember,
  ProposalListMemberStatus,
  ProposalPageFilterOption,
  ProposalPageSortOption,
  State as ReduxState
} from 'types';
import type {
  ProposalTileChannelType,
  ProposalTileChannelTypeOptions,
  ProposalTileType
} from 'modules/proposal/types/ProposalTile.type';

import './proposal-create-page.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./proposal-create-page.i18n.json'));

const POSTS_PER_TILE = {
  instagram: POSTS_AMOUNT_INSTAGRAM,
  tiktok: POSTS_AMOUNT_TIKTOK
};

type OwnProps = {
  location: LocationWindow
};

type StateProps = {
  list: List,
  listInfo: ProposalListInfo | undefined,
  creatorList: Array<ProposalListMember>,
  organizationsListIds: Array<number>,
  loadingProfileIds: { [profileId: number]: boolean },
  paginationMeta: PaginationMeta,
  paginationLinks: PaginationLinks,
  listLoadingStatus: boolean,
  infoLoadingStatus: boolean,
  isReportDownloading: boolean
};

type DispatchProps = {
  dispatchFetchList: (orgId: number, listId: number) => void,
  dispatchResetLists: () => void,
  dispatchResetCreatorList: () => void,
  dispatchResetCreatorListInfo: () => void,
  dispatchLoadCreatorList: (orgId: number, listId: number, page: number, filter: ProposalPageFilterOption, sort: ProposalPageSortOption) => void,
  dispatchLoadCreatorListInfo: (orgId: number, listId: number) => Promise<ProposalListInfo>,
  dispatchUpdateCreatorListInfo: (orgId: number, listId: number, data: ProposalListInfo) => Promise<void>,
  dispatchSetMedia: (
    organizationId: number,
    listId: number,
    creatorId: number,
    media: Array<ProposalImage>,
    channelType: ProposalTileChannelType
  ) => Promise<void>,
  dispatchUpdateMember: (organizationId: number, listId: number, entry: ProposalListMember) => Promise<void>,
  dispatchNoteSaved: () => void,
  dispatchDeleteInfluencerFromList: (orgId: number, listId: number, influencerId: number) => Promise<any>,
  dispatchShowConfirmationModal: (props: ListConfirmModalProps) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchGetRecentMediaExceptGivenUrls: (
    profileId: number,
    channelId: number,
    numberOfImages: number,
    ignoredUrls: Array<string>,
    triggerError
  ) => Promise<Array<ProposalImage>>,
  dispatchLoadAllCreatorsContent: (
    organizationId: number,
    listId: number,
    page: number,
    filter: ProposalPageFilterOption,
    sort: ProposalPageSortOption
  ) => Promise<void>,
  dispatchDownloadCreatorProposal: (
    organizationId: number,
    listId: number,
    sortOrder: ProposalPageSortOption,
    currentFilter: ProposalPageFilterOption
  ) => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  currentSort: ProposalPageSortOption,
  currentFilter: ProposalPageFilterOption
};

class ProposalCreatePage extends React.PureComponent<Props, State> {
  state: State = {
    currentFilter: 'all',
    currentSort: 'ig_desc'
  };

  componentDidMount() {
    const {
      dispatchFetchList,
      dispatchLoadCreatorListInfo,
      dispatchResetLists,
      dispatchResetCreatorList,
      dispatchResetCreatorListInfo
    } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();

    const isNilOrNaN = (val) => isNil(val) || Number.isNaN(val);

    if (!isNilOrNaN(orgId) && !isNilOrNaN(listId)) {
      dispatchResetLists();
      dispatchResetCreatorList();
      dispatchResetCreatorListInfo();

      dispatchFetchList(orgId, listId);
      dispatchLoadCreatorListInfo(orgId, listId)
        .then((listInfo) => this.setInitialSortAndFetch(listInfo));
    }
  }

  private setInitialSortAndFetch(newListInfo: ProposalListInfo): void {
    const query = this.props.location.query || {};

    const pageToOpen = query.page || 1;
    const sortToUse = query.sort || 'ig_desc';
    const filterToUse = query.filter || 'all';

    const newSort = getNewSort(newListInfo?.channels ?? DEFAULT_LIST_CHANNELS, sortToUse);

    this.setState({
      currentSort: newSort,
      currentFilter: filterToUse
    }, () => {
      this.loadListPage(pageToOpen);
    });
  }

  private getIdsFromUrl(): { orgId: number, listId: number } {
    const { location } = this.props;
    const { listId } = location.params || {};
    const orgId = Number(location?.query?.orgId);

    return {
      orgId,
      listId
    };
  }

  private loadListPage = (page: number = 1): void => {
    const { dispatchLoadCreatorList } = this.props;
    const { currentSort, currentFilter } = this.state;
    const { orgId, listId } = this.getIdsFromUrl();

    if (!isNil(orgId) && !isNil(listId) && !isNaN(orgId)) {
      dispatchLoadCreatorList(orgId, listId, page, currentFilter, currentSort);
      setTimeout(() => {
        // oh..
        navigate.replace(`/collections/creators/${listId}/proposal?page=${page}&sort=${currentSort}&filter=${currentFilter}&orgId=${orgId}`);
      }, 100);
    }
  };

  private autoFillAllCreators = (): void => {
    const { dispatchLoadAllCreatorsContent, location } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();
    const query = location.query || {};
    const page = query.page || 1;
    const { currentSort, currentFilter } = this.state;

    dispatchLoadAllCreatorsContent(orgId, listId, page, currentFilter, currentSort);
  };

  private handleFilterReset = (): void => {
    this.setState({
      currentSort: 'ig_desc',
      currentFilter: 'all'
    }, () => {
      this.loadListPage(1);
    });
  };

  private updateListInfo = (newTemplateType: string): void => {
    const {
      listInfo,
      dispatchUpdateCreatorListInfo
    } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();

    if (!isNil(listInfo) && !isNil(orgId) && !isNil(listId)) {
      dispatchUpdateCreatorListInfo(orgId, listId, {
        ...listInfo,
        templateType: newTemplateType
      });
    }
  };

  private updateHeadlinesSetting = (isShowHeadlines: boolean): void => {
    const {
      listInfo,
      dispatchUpdateCreatorListInfo
    } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();

    if (!isNil(listInfo) && !isNil(orgId) && !isNil(listId)) {
      dispatchUpdateCreatorListInfo(orgId, listId, {
        ...listInfo,
        isShowHeadlines
      });
    }
  };

  private updateListSelectedChannels = async (selectedChannels: Array<ProposalTileChannelTypeOptions>): Promise<void> => {
    const {
      listInfo,
      location,
      dispatchUpdateCreatorListInfo
    } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();

    if (!isNil(listInfo) && !isNil(orgId) && !isNil(listId)) {
      await dispatchUpdateCreatorListInfo(orgId, listId, {
        ...listInfo,
        channels: selectedChannels
      });

      // refetch list since channel update affect pending statuses
      const query = location.query || {};
      const pageToOpen = query.page || 1;

      this.setState((state) => ({
        currentSort: getNewSort(selectedChannels, state.currentSort)
      }), () => {
        this.loadListPage(pageToOpen);
      });
    }
  };

  private async handleMediaReset(entry: ProposalListMember): Promise<void> {
    const { dispatchSetMedia } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();
    if (!isNil(orgId) && !isNil(listId)) {
      await dispatchSetMedia(orgId, listId, entry.id, [], 'instagram');
      await dispatchSetMedia(orgId, listId, entry.id, [], 'tiktok');
    }
  }

  private handleNewMediaSet(
    image: ProposalImage | null,
    index: number,
    entry: ProposalListMember,
    channelType: ProposalTileChannelType
  ): void {
    const { dispatchSetMedia } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();
    if (!isNil(orgId) && !isNil(listId)) {
      const images = entry.images[channelType];
      const emptyImage = { url: null } as ProposalImage;
      const updatedImage = { ...(image || emptyImage), isLoading: true };

      // either create new array of nulls or take original array if it exists
      const currentImagesData: Array<ProposalImage> = (!images || images.length === 0)
        ? fill<ProposalImage>(Array(POSTS_PER_TILE[channelType]), emptyImage)
        : [...images];

      currentImagesData[index] = updatedImage ?? emptyImage;
      dispatchSetMedia(orgId, listId, entry.id, currentImagesData, channelType);
    }
  }

  private getNewStatus(entry: ProposalListMember): ProposalListMemberStatus {
    if (entry.approvalStatus === 'approved' || entry.approvalStatus === 'declined') {
      return entry.approvalStatus;
    }

    const isIncomplete = isTileIncomplete(this.props.listInfo?.channels ?? DEFAULT_LIST_CHANNELS, entry);
    if (isIncomplete) {
      return 'incomplete';
    }

    return 'pending';
  }

  private handleMemberChannelUpdate(entry: ProposalListMember, selectedChannels: Array<ProposalTileChannelType>): void {
    const { dispatchUpdateMember } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();

    const entryWithNewChannels = {
      ...entry,
      selectedChannels
    };
    const newStatus = this.getNewStatus(entryWithNewChannels);

    dispatchUpdateMember(orgId, listId, {
      ...entryWithNewChannels,
      approvalStatus: newStatus
    });
  }

  private handleNoteUpdate(entry: ProposalListMember, note?: string | null): void {
    const { dispatchUpdateMember, dispatchNoteSaved } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();

    dispatchUpdateMember(orgId, listId, {
      ...entry,
      accountManagerNote: note
    }).then(dispatchNoteSaved);
  }

  private autofillChannel(entry: ProposalListMember, channel: ProposalTileChannelType, triggerError: boolean = true): Promise<void> {
    const { dispatchSetMedia, dispatchGetRecentMediaExceptGivenUrls } = this.props;
    const creatorProfile = entry.creatorProfile;
    const channelId = creatorProfile.channels[channel]?.id;
    const { orgId, listId } = this.getIdsFromUrl();

    if (!channelId || isNil(orgId) || isNil(listId)) {
      return Promise.resolve();
    }

    const images = entry.images[channel] ?? [];
    const filledUrls = images.reduce<Array<string>>((result, image) => {
      if (image?.url && image?.imageUrl) {
        return result.concat([image.url]);
      }
      return result;
    }, []);

    const imagesToFill = POSTS_PER_TILE[channel] - filledUrls.length;

    if (imagesToFill <= 0) {
      return Promise.resolve();
    }

    return dispatchGetRecentMediaExceptGivenUrls(
      creatorProfile.id,
      channelId,
      imagesToFill, // need to fill only unfilled
      filledUrls,
      triggerError
    ).then((data: Array<ProposalImage>) => {
      const emptyImage: ProposalImage = { url: null, imageUrl: null };

      const currentImagesData = (images.length === 0)
        ? fill(Array(POSTS_PER_TILE[channel]), emptyImage)
        : entry.images[channel].slice();

      if (currentImagesData.length < POSTS_PER_TILE[channel]) {
        // when filling legacy images
        for (let i = currentImagesData.length; i < POSTS_PER_TILE[channel]; i++) {
          currentImagesData.push(emptyImage);
        }
      }

      let nextImageToFill = 0;
      const resultedImageSet = (currentImagesData ?? [])
        .map((image) => {
          if (!image || !image.imageUrl) {
            return data[nextImageToFill++];
          }
          return image;
        });

      return dispatchSetMedia(orgId, listId, entry.id, resultedImageSet, channel);
    });
  }

  private async handleAutofill(entry: ProposalListMember): Promise<void> {
    const { listInfo } = this.props;
    const canHaveCustomChannels = this.canHaveCustomChannels();
    const tileCustomChannels = this.getEntryCustomChannels(entry);
    const activeChannels = collectActiveChannels(
      entry.creatorProfile,
      listInfo?.channels ?? DEFAULT_LIST_CHANNELS,
      tileCustomChannels ?? DEFAULT_MEMBER_CUSTOM_CHANNELS,
      canHaveCustomChannels
    ).map(channel => channel.channelType);

    for (let i = 0; i < activeChannels.length; i++) {
      const channel = activeChannels[i];
      // eslint-disable-next-line no-await-in-loop
      await this.autofillChannel(entry, channel);
    }
  }

  private async handleAutoFillForNotAvailableImages(
    entry: ProposalListMember,
    availableImages: Array<ProposalImage>,
    channelType: string) {
    const { listInfo } = this.props;
    const canHaveCustomChannels = this.canHaveCustomChannels();
    const tileCustomChannels = this.getEntryCustomChannels(entry);
    const activeChannels = collectActiveChannels(
      entry.creatorProfile,
      listInfo?.channels ?? DEFAULT_LIST_CHANNELS,
      tileCustomChannels ?? DEFAULT_MEMBER_CUSTOM_CHANNELS,
      canHaveCustomChannels
    ).map(channel => channel.channelType);

    for (let i = 0; i < activeChannels.length; i++) {
      const channel = activeChannels[i];
      // eslint-disable-next-line no-await-in-loop
      await this.autofillChannel(
        { ...entry, images: { ...entry.images, [channelType]: availableImages } },
        channel,
        false
      );
    }
  }

  private handleFilterChange = (newFilter: ProposalPageFilterOption) => {
    this.setState({
      currentFilter: newFilter
    }, () => {
      this.loadListPage(1);
    });
  };

  private handleSortChange = (newSort: ProposalPageSortOption) => {
    this.props.dispatchResetCreatorList();

    this.setState({
      currentSort: newSort
    }, () => {
      this.loadListPage(1);
    });
  };

  private handleDelete = (influencer: ProposalCreatorProfile, hasProposalStatus: boolean) => {
    const {
      list,
      location,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal,
      dispatchDeleteInfluencerFromList
    } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();
    const query = location.query || {};

    const pageToOpen = query.page || 1;

    dispatchShowConfirmationModal({
      title: __t('proposal_create_page.modal_delete_influencer.title', { listName: list.label }),
      message: '',
      confirmButtonLabel: __t('proposal_create_page.modal_delete_influencer.button_remove'),
      innerContent: this.renderConfirmationModal(influencer, hasProposalStatus),
      onConfirm: () => {
        dispatchDeleteInfluencerFromList(orgId, listId, influencer.id)
          .then(() => this.loadListPage(pageToOpen))
          .then(() => window.scrollTo(0, 0));

        dispatchHideConfirmationModal();
      },
      onCancel: () => {
        dispatchHideConfirmationModal();
      }
    });
  };

  private handleReportDownload = () => {
    const { dispatchDownloadCreatorProposal } = this.props;
    const { currentSort, currentFilter } = this.state;
    const { orgId, listId } = this.getIdsFromUrl();
    dispatchDownloadCreatorProposal(orgId, listId, currentSort, currentFilter);
  };

  private getListManageStatus(): boolean {
    const { orgId } = this.getIdsFromUrl();
    const { list, organizationsListIds } = this.props;

    return getManageListStatus(Number(orgId), organizationsListIds) && list.type === 'custom';
  }

  private canHaveCustomChannels(): boolean {
    return this.props.listInfo?.channels.includes('custom') ?? false;
  }

  private getEntryCustomChannels(entry: ProposalListMember): Array<ProposalTileChannelType> | undefined {
    return this.canHaveCustomChannels()
      ? entry.selectedChannels ?? DEFAULT_MEMBER_CUSTOM_CHANNELS
      : undefined;
  }

  private renderConfirmationModal = (influencer: ProposalCreatorProfile, hasProposalStatus: boolean) => (
    <React.Fragment>
      {/* @ts-ignore - not fully compatible, but fits */}
      <ModalFrameEntity influencerProfile={influencer} />
      <ModalFrameMessage
        message={__t('proposal_create_page.modal_delete_influencer.message')}
      />
      {
        hasProposalStatus && (
          <ModalFrameMessage
            message={__t('proposal_create_page.modal_delete_influencer.approved_proposal_influencer_message')}
          />
        )
      }
    </React.Fragment>
  );

  private renderEntry(entry: ProposalListMember): React.ReactNode {
    const { list, listInfo, loadingProfileIds } = this.props;
    const { orgId, listId } = this.getIdsFromUrl();
    const creatorProfile = entry.creatorProfile;
    const manageListStatus = this.getListManageStatus();
    const isCustomList = (list.type && list.type === 'custom') || false;

    const canHaveCustomChannels = this.canHaveCustomChannels();
    const canHaveCustomChannelsSelector = canHaveCustomChannels
      && (
        entry.approvalStatus === 'pending'
        || entry.approvalStatus === 'incomplete'
      );
    const tileCustomChannels = this.getEntryCustomChannels(entry);

    return (
      <React.Fragment key={entry.id}>
        <ProposalTile
          creatorProfile={creatorProfile}
          status={entry.approvalStatus}
          isFavorite={entry.isPreferred}
          onMediaReset={() => this.handleMediaReset(entry)}
          onMediaSet={
            (image, index, channelType) =>
              this.handleNewMediaSet(image, index, entry, channelType)
          }
          posts={entry.images.instagram || []}
          postsTikTok={entry.images.tiktok || []}
          template={(listInfo?.templateType as ProposalTileType) ?? 'basic'}
          notes={{
            brandRepresentative: entry.brandRepresentativeNote,
            accountManager: entry.accountManagerNote
          }}
          onNoteUpdate={(note) => this.handleNoteUpdate(entry, note)}
          onAutofill={(availableImages?: Array<ProposalImage>, channelType?: ProposalTileChannelType) => {
            if (availableImages && channelType) {
              return this.handleAutoFillForNotAvailableImages(entry, availableImages, channelType);
            }
            return this.handleAutofill(entry);
          }}
          canDelete={manageListStatus && isCustomList}
          onDelete={() => this.handleDelete(creatorProfile, hasProposalPage(entry.approvalStatus))}
          contextMenuProps={{
            orgId,
            listId
          }}
          isForBrand={false}
          canHaveChannelsSelector={canHaveCustomChannelsSelector}
          customChannels={tileCustomChannels}
          channelOptions={listInfo?.channels ?? DEFAULT_LIST_CHANNELS}
          onSelectedChannelChange={
            (channels) =>
              this.handleMemberChannelUpdate(entry, channels)
          }
          isLoading={loadingProfileIds[entry.creatorProfile.id]}
        />
        <VerticalSpacing type="large" />
      </React.Fragment>
    );
  }

  render() {
    const {
      location,
      listInfo,
      creatorList,
      paginationMeta,
      paginationLinks,
      listLoadingStatus,
      infoLoadingStatus,
      isReportDownloading
    } = this.props;
    const { currentFilter, currentSort } = this.state;

    const { listId } = location.params || {};
    const orgId = Number(location?.query?.orgId);

    const hasApprovedProfiles = creatorList.some((entry) => entry.approvalStatus === 'approved');
    const groupedListData = getGroupedMembers(creatorList, currentSort);

    const linkToList = `/collections/creators/${listId}?orgId=${orgId}`;

    const breadCrumbsItems: Array<Breadcrumb> = [
      {
        label: __t('proposal_create_page.root_breadcrumb'),
        link: '/collections/creators'
      },
      {
        label: listInfo?.creatorListName ?? '',
        link: linkToList
      },
      {
        label: __t('proposal_create_page.current_page_breadcrumb'),
        link: window.location.href
      }
    ];

    const isShowingHeadlines = listInfo?.isShowHeadlines ?? true;
    const hasNoResults = !listLoadingStatus
      && !infoLoadingStatus
      && listInfo
      && groupedListData.length === 0;

    const filterContainerClasses = classnames('proposal-create-page__filter', {
      'proposal-create-page__filter--with-headlines': isShowingHeadlines
    });

    return (
      <Page
        documentTitle={__t('create_proposal_page_title')}
      >
        {(listLoadingStatus || infoLoadingStatus) && <Spinner />}
        <div className="proposal-create-page__header">
          <Breadcrumbs items={breadCrumbsItems} />
          <div className="proposal-create-page__close-button">
            <Tooltip
              message={__t('proposal_create_page.label_view_creator_list')}
              width="short"
            >
              <SVGDynamicIcon
                name="cross-custom-fg"
                width={14}
                height={14}
                customColor="grey"
                link={linkToList}
              />
            </Tooltip>
          </div>
        </div>
        <VerticalSpacing type="medium" />
        <ProposalPageHeader
          hasYoutubeOnly // okay for first iteration
          hasApprovedCreators={hasApprovedProfiles}
          organizationId={this.getIdsFromUrl().orgId}
          listInfo={listInfo}
          currentTemplate={listInfo?.templateType}
          clientApprovalLink={listInfo?.creatorApprovalLink}
          onTemplateChange={this.updateListInfo}
          canUpdateChannels={!listInfo?.hasReviewedCreators}
          currentChannels={listInfo?.channels || []}
          onChannelChange={this.updateListSelectedChannels}
          shouldShowHeadlines={isShowingHeadlines}
          onHeadlineChange={this.updateHeadlinesSetting}
          isReportDownloading={isReportDownloading}
          onReportDownload={this.handleReportDownload}
        />
        <div className={filterContainerClasses}>
          <ProposalTileFilter
            currentChannels={listInfo?.channels || []}
            sort={currentSort}
            filter={currentFilter}
            onSortChange={this.handleSortChange}
            onFilterChange={this.handleFilterChange}
          />
          <AutofillButton
            onAutofill={() => this.autoFillAllCreators()}
            loadAllContent
          />
        </div>
        <div className="proposal-create-page__content">
          {hasNoResults && (
            <ProposalNoResults
              canReset={currentFilter !== 'all'}
              onReset={this.handleFilterReset}
            />
          )}
          {
            groupedListData
              .map((entry, index) => {
                const entries = entry.data.map(this.renderEntry, this);
                return (
                  <React.Fragment key={`group-${entry.tier}-${index}`}>
                    {isShowingHeadlines && (
                      <div className="proposal-create-page__tier-label">
                        {__t(`proposal_create_page.tiers.${entry.tier}`)}
                      </div>
                    )}
                    {entries}
                  </React.Fragment>
                );
              })
          }
        </div>
        <Pagination
          location={location}
          meta={paginationMeta}
          links={paginationLinks}
          onClick={this.loadListPage}
        />
      </Page>
    );
  }
}

const mapStateToProps = ({ lists, organizations, proposalCreatePage, proposalCreatePageInfo, proposalReport }: ReduxState): StateProps => ({
  list: lists.currentList.list,
  listInfo: proposalCreatePageInfo.info,
  creatorList: proposalCreatePage.list,
  paginationMeta: proposalCreatePage.paginationMeta,
  paginationLinks: proposalCreatePage.paginationLinks,
  listLoadingStatus: proposalCreatePage.loadingStatus,
  infoLoadingStatus: proposalCreatePageInfo.loadingStatus,
  loadingProfileIds: proposalCreatePage.loadingProfileIds,
  organizationsListIds: organizations.list.map(organization => organization.id),
  isReportDownloading: proposalReport.isDownloading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchList: (orgId: number, listId: number): void => dispatch(fetchList(orgId, listId)),

  dispatchDeleteInfluencerFromList: (orgId: number, listId: number, influencerId: number) => (
    dispatch(removeSingleInfluencerFromList(orgId, listId, influencerId))
  ),

  dispatchLoadCreatorListInfo: (orgId: number, listId: number): Promise<ProposalListInfo> =>
    dispatch(loadCreatorListInfo(orgId, listId)),
  dispatchUpdateCreatorListInfo: (orgId: number, listId: number, data: ProposalListInfo): Promise<void> =>
    dispatch(updateListInfo(orgId, listId, data)),
  dispatchLoadCreatorList: (orgId: number, listId: number, page: number, filter: ProposalPageFilterOption, sort: ProposalPageSortOption): void => dispatch(loadCreatorList(orgId, listId, page, filter, sort)),

  dispatchSetMedia: (
    organizationId: number,
    listId: number,
    creatorId: number,
    media: Array<ProposalImage>,
    channelType: ProposalTileChannelType
  ): Promise<void> => dispatch(setMedia(organizationId, listId, creatorId, media, channelType)),

  dispatchUpdateMember: (organizationId: number, listId: number, entry: ProposalListMember): Promise<void> =>
    dispatch(updateMemberEntry(organizationId, listId, entry)),
  dispatchNoteSaved: (): void => dispatch(success(__t('proposal_create_page.note_saved_popup'), { hideDuration: 2000 })),
  dispatchResetLists: (): void => dispatch(resetLists()),
  dispatchResetCreatorList: (): void => dispatch(resetList()),
  dispatchResetCreatorListInfo: (): void => dispatch(resetInfo()),

  dispatchShowConfirmationModal: (props: ListConfirmModalProps) => (
    dispatch(showConfirmModal(props))
  ),

  dispatchHideConfirmationModal: () => (
    dispatch(hideConfirmModal())
  ),

  dispatchGetRecentMediaExceptGivenUrls: (
    profileId: number,
    channelId: number,
    numberOfImages: number,
    ignoredUrls: Array<string>,
    triggerError?: boolean
  ): Promise<Array<ProposalImage>> =>
    dispatch(getRecentMediaExceptGivenUrls(profileId, channelId, numberOfImages, ignoredUrls, triggerError)),

  dispatchLoadAllCreatorsContent: (
    organizationId: number,
    listId: number,
    page: number,
    filter: ProposalPageFilterOption,
    sort: ProposalPageSortOption
  ): Promise<void> => dispatch(loadAllCreatorsContent(organizationId, listId, page, filter, sort)),
  dispatchDownloadCreatorProposal: (
    organizationId: number,
    listId: number,
    sortOrder: ProposalPageSortOption,
    currentFilter: ProposalPageFilterOption
  ) => dispatch(downloadCreatorProposal(organizationId, listId, sortOrder, currentFilter))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProposalCreatePage);
